import { createApi } from "@/services/api";
import { useMemo, useRef, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { logger } from "@/utils/logger";

type ApiReturnType = ReturnType<typeof createApi>;

export function useApi(): ApiReturnType {
  const { aiewToken, refreshToken, logout } = useAuth();

  // Use refs to store the latest token and functions without triggering re-memoization
  const tokenRef = useRef(aiewToken);
  const refreshTokenRef = useRef(refreshToken);
  const logoutRef = useRef(logout);

  // Update refs when values change
  useEffect(() => {
    tokenRef.current = aiewToken;
  }, [aiewToken]);

  useEffect(() => {
    refreshTokenRef.current = refreshToken;
  }, [refreshToken]);

  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  useEffect(() => {
    logger.debug("useApi: Creating API instance", { hasToken: !!tokenRef.current });
  }, []);

  const api = useMemo(() => {
    return createApi(
      // getToken callback - always fetch latest token from ref
      async () => {
        return tokenRef.current;
      },
      // refreshToken callback for 401/403 errors
      async () => {
        logger.warn("API request failed with 401/403. Attempting token refresh before logout...");
        try {
          // Attempt to refresh the token from AIEW
          // This will update tokenRef.current via the effect above
          const newToken = await refreshTokenRef.current();
          if (newToken) {
            logger.info("✅ Token refresh successful, new token will be used for retry");
            // Token was refreshed successfully
            return;
          }
        } catch (error) {
          logger.error("Token refresh failed:", error);
        }

        // If refresh fails or returns no token, proceed with logout
        logger.error("Token refresh failed. Logging out via AIEW.");
        logoutRef.current();
      }
    );
  }, []); // Empty deps - API instance created once, uses refs for latest values

  return api;
}
